import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ReactComponent as ViewIcon } from "../../../assets/icons/eye.svg";
import { ReactComponent as EditIcon } from "../../../assets/icons/message-edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/trash.svg";
import { ReactComponent as PlusIcon } from "../../../assets/icons/plus.svg";
import { ReactComponent as SearchIcon } from "../../../assets/icons/search-normal.svg";
import { ReactComponent as NoDataTile } from "../../../assets/icons/no-data.svg";
import "./devices.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeletePopup from "../../../components/deletePopup/deletePopup";
import ViewPopupWrapper from "../../../components/viewPopupWrapper/viewPopupWrapper";
import {
  fetchDeviceDetailsAsync,
  fetchDevicesAsync,
} from "../../../redux/reducers/devicesSlice";
import PaginationComponent from "../../../components/pagination/pagination";
import ApiLoader from "../../../components/apiLoader/apiLoader";

const TableHeader = React.memo(() => {
  const headers = ["Device name", "Device type", "SKU", "Price", "Action"];
  return (
    <TableHead>
      <TableRow className="plans-header-row">
        {headers.map((header, index) => (
          <TableCell
            key={header}
            className={`plans-table-header ${
              index === 0 ? "plans-table-header-first-col" : ""
            }${index === 4 ? "plans-table-header-last-col" : ""}`}
          >
            {header}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

const TableRowData = React.memo(
  ({ row, setIsViewPopupVisible, refreshList }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);

    const handleViewClick = () => {
      dispatch(fetchDeviceDetailsAsync(row.id));
      setIsViewPopupVisible(true);
    };

    const handleEditClick = () => {
      navigate(`/edit-device/${row.id}`);
    };

    const handleDeletePopupOpen = () => {
      setIsDeletePopupVisible(true);
    };
    const handleDeletePopupClose = () => {
      setIsDeletePopupVisible(false);
    };
    return (
      <TableRow className="plans-table-row">
        <TableCell>{row.planName}</TableCell>
        <TableCell>{row.deviceType}</TableCell>
        <TableCell>{row.sku}</TableCell>
        <TableCell>{row.price}</TableCell>
        <TableCell className="plans-menu-items">
          <IconButton onClick={handleViewClick}>
            <ViewIcon />
          </IconButton>
          <IconButton onClick={handleEditClick}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={handleDeletePopupOpen}>
            <DeleteIcon />
            {isDeletePopupVisible && (
              <DeletePopup
                onClose={handleDeletePopupClose}
                id={row?.id}
                name={"device"}
                refreshList={refreshList}
              />
            )}
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }
);

const Devices = () => {
  const [isViewPopupVisible, setIsViewPopupVisible] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);

  const dispatch = useDispatch();
  const { deviceData, isLoading } = useSelector((state) => ({
    deviceData: state?.deviceData?.devicesData,
    isLoading: state?.deviceData?.isDevicesLoading,
  }));

  const deviceDetails = useSelector(
    (state) => state?.deviceData?.selectedDevice
  );
  const deleteMessage = useSelector(
    (state) => state?.deviceData?.deleteMessage
  );
  const navigate = useNavigate();
  const totalPages = Math.round(deviceData?.total_count / rowsPerPage);

  const handleCreatePlan = () => {
    navigate("/add-device");
  };

  const handleChange = (event) => {
    setSearchValue(event.target.value);
  };

  useEffect(() => {
    // Set a delay before updating
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  useEffect(() => {
    dispatch(
      fetchDevicesAsync({
        page,
        limit: rowsPerPage,
        searchtext: debouncedSearch,
      })
    );
  }, [dispatch, page, rowsPerPage, deleteMessage, debouncedSearch]);

  const refreshDevicesList = () => {
    dispatch(
      fetchDevicesAsync({
        page,
        limit: rowsPerPage,
        searchtext: debouncedSearch,
      })
    );
  };

  const formattedDeviceData = deviceData?.devices?.map((plan) => ({
    id: plan.id,
    planName: plan.name,
    deviceType: plan.device_type,
    sku: plan.sku,
    price: `$${parseFloat(plan.price).toFixed(2)}`,
  }));

  const formattedViewPlanDetails = [
    [
      {
        title: "Device name",
        value: deviceDetails?.name,
        cols: 1,
        alignment: "left",
      },
      {
        title: "Device type",
        value: deviceDetails?.device_type,
        cols: 1,
        alignment: "left",
      },
      { title: "SKU", value: deviceDetails?.sku, cols: 1, alignment: "center" },
    ],
    [
      {
        title: "price",
        value: `$${parseFloat(deviceDetails?.price || 0).toFixed(2)}`,
        cols: 1,
        alignment: "right",
      },
      {
        title: "Description",
        value: deviceDetails?.description,
        cols: 2,
        alignment: "left",
      },
    ],
  ];

  const closeViewModal = () => {
    setIsViewPopupVisible(false);
  };

  return (
    <div className="plans-table">
      <ApiLoader loading={isLoading} />

      <ViewPopupWrapper
        isOpen={isViewPopupVisible}
        onClose={closeViewModal}
        title="Plan Details"
        columns={3}
        rows={formattedViewPlanDetails}
      />
      <div className="header-section">
        <p className="plans-title">Mobile Devices</p>
      </div>
      <div className="sub-header-section">
        <TextField
          variant="outlined"
          placeholder="Search devices"
          fullWidth
          value={searchValue}
          onChange={handleChange}
          sx={{
            borderRadius: "50px",
            border: "1px solid #EBEEF2",
            width: "40%",
            height: "40px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "25px",
              paddingLeft: "12px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiInputBase-input": {
              color: "#A3AAB2",
              padding: 0,
              height: "40px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <button onClick={handleCreatePlan} className="create-plan-btn">
          Add Device
          <PlusIcon />
        </button>
      </div>
      <Paper className="plans-table-paper">
        <TableContainer>
          <Table>
            <TableHeader />
            <TableBody>
              {formattedDeviceData?.length > 0 ? (
                formattedDeviceData?.map((plan, index) => (
                  <TableRowData
                    key={index}
                    row={plan}
                    setIsViewPopupVisible={setIsViewPopupVisible}
                    refreshList={refreshDevicesList}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} >
                    <div className="no-data-admin">
                      <NoDataTile />
                    </div>{" "}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <div className="device-pagination">
        <PaginationComponent
          totalPages={totalPages}
          page={page}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </div>
    </div>
  );
};

export default Devices;
